/**
 * PolicyBuilder - Visual Policy Editor Component
 *
 * Provides a form-based interface for building policy statements without writing JSON.
 * Users can create Allow/Deny statements with principals and resources.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Form, Button, Card, Badge, Alert, Row, Col, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { PolicyDocument, PolicyStatement, Entity } from '@/types/policy';
import type { PaginatedResponse } from '@/services/apiClient';
import { apiClient, StudySummary, ClinicSummary, DeviceSummary, DataTypeSummary } from '@/services/apiClient';
import PrincipalSelector from './PrincipalSelector';
import ServerSearchableDropdown from '@/components/common/ServerSearchableDropdown';

interface PolicyBuilderProps {
  onPolicyChange: (policy: PolicyDocument) => void;
  initialPolicy?: PolicyDocument;
  studies?: Entity[];
  clinics?: Entity[];
  devices?: Entity[];
  dataTypes?: Entity[];
  users?: Entity[];
  userGroups?: Entity[];
  roles?: Entity[];
  onRefreshStudies?: () => void | Promise<void>;
  onRefreshClinics?: () => void | Promise<void>;
  onRefreshDevices?: () => void | Promise<void>;
  onRefreshDataTypes?: () => void | Promise<void>;
  onRefreshUsers?: () => void | Promise<void>;
  onRefreshUserGroups?: () => void | Promise<void>;
  onRefreshRoles?: () => void | Promise<void>;
  isRefreshingStudies?: boolean;
  isRefreshingClinics?: boolean;
  isRefreshingDevices?: boolean;
  isRefreshingDataTypes?: boolean;
  isRefreshingUsers?: boolean;
  isRefreshingUserGroups?: boolean;
  isRefreshingRoles?: boolean;
}

export default function PolicyBuilder({
  onPolicyChange,
  initialPolicy,
  studies = [],
  clinics = [],
  devices = [],
  dataTypes = [],
  users = [],
  userGroups = [],
  roles = [],
  onRefreshStudies,
  onRefreshClinics,
  onRefreshDevices,
  onRefreshDataTypes,
  onRefreshUsers,
  onRefreshUserGroups,
  onRefreshRoles,
  isRefreshingStudies = false,
  isRefreshingClinics = false,
  isRefreshingDevices = false,
  isRefreshingDataTypes = false,
  isRefreshingUsers = false,
  isRefreshingUserGroups = false,
  isRefreshingRoles = false,
}: PolicyBuilderProps) {
  const { t } = useTranslation();
  const [policyVersion] = useState(initialPolicy?.Version || '2025-01-01');
  const [statements, setStatements] = useState<PolicyStatement[]>(
    initialPolicy?.Statement || []
  );

  // Use refs to prevent circular update loops
  const onPolicyChangeRef = useRef(onPolicyChange);
  const isInitializedRef = useRef(false);
  const lastInitialPolicyRef = useRef<string | null>(null);

  // Keep callback ref updated
  useEffect(() => {
    onPolicyChangeRef.current = onPolicyChange;
  }, [onPolicyChange]);

  // Only sync with initialPolicy on first mount or when it meaningfully changes
  useEffect(() => {
    const initialPolicyJson = initialPolicy ? JSON.stringify(initialPolicy.Statement) : null;

    // Skip if this is the same policy we already processed
    if (lastInitialPolicyRef.current === initialPolicyJson) {
      return;
    }

    // Update the last processed policy
    lastInitialPolicyRef.current = initialPolicyJson;

    if (initialPolicy?.Statement) {
      setStatements(initialPolicy.Statement);
    }
  }, [initialPolicy]);

  // Notify parent when statements change (but not on initial mount)
  useEffect(() => {
    // Skip the initial notification to prevent loops
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      return;
    }

    // Use the ref to avoid dependency on onPolicyChange
    onPolicyChangeRef.current({
      Version: policyVersion,
      Statement: statements,
    });
  }, [statements, policyVersion]);

  // Current statement being built
  const [currentStatement, setCurrentStatement] = useState<{
    effect: 'Allow' | 'Deny';
    principal: {
      users: string[];
      userGroups: string[];
      roles: string[];
    };
    studies: Array<{ id: string; start?: string; end?: string }>;
    clinics: Array<{ id: string; start?: string; end?: string }>;
    devices: Array<{ id: string; start?: string; end?: string }>;
    dataTypes: Array<{ id: string }>;
  }>({
    effect: 'Allow',
    principal: {
      users: [],
      userGroups: [],
      roles: [],
    },
    studies: [],
    clinics: [],
    devices: [],
    dataTypes: [],
  });

  // Memoized entity lookups for display
  // Ensure props are arrays before calling forEach
  const userLookup = useMemo(() => {
    const map = new Map<string, Entity>();
    const safeUsers = Array.isArray(users) ? users : [];
    safeUsers.forEach((u) => {
      map.set(u.id.toString(), u);
      if (u.email) map.set(u.email.toLowerCase(), u);
    });
    return map;
  }, [users]);

  const userGroupLookup = useMemo(() => {
    const map = new Map<string, Entity>();
    const safeUserGroups = Array.isArray(userGroups) ? userGroups : [];
    safeUserGroups.forEach((g) => {
      map.set(g.id.toString(), g);
      if (g.name) map.set(g.name, g);
    });
    return map;
  }, [userGroups]);

  const roleLookup = useMemo(() => {
    const map = new Map<string, Entity>();
    const safeRoles = Array.isArray(roles) ? roles : [];
    safeRoles.forEach((r) => {
      map.set(r.id.toString(), r);
      if (r.name) map.set(r.name, r);
    });
    return map;
  }, [roles]);

  const studyLookup = useMemo(() => {
    const map = new Map<string, Entity>();
    const safeStudies = Array.isArray(studies) ? studies : [];
    safeStudies.forEach((s) => {
      map.set(s.id.toString(), s);
      if (s.study_id) map.set(s.study_id, s);
    });
    return map;
  }, [studies]);

  const clinicLookup = useMemo(() => {
    const map = new Map<string, Entity>();
    const safeClinics = Array.isArray(clinics) ? clinics : [];
    safeClinics.forEach((c) => {
      map.set(c.id.toString(), c);
      if (c.clinic_id) map.set(c.clinic_id, c);
    });
    return map;
  }, [clinics]);

  const deviceLookup = useMemo(() => {
    const map = new Map<string, Entity>();
    const safeDevices = Array.isArray(devices) ? devices : [];
    safeDevices.forEach((d) => {
      map.set(d.id.toString(), d);
      if (d.device_id) map.set(d.device_id, d);
    });
    return map;
  }, [devices]);

  const dataTypeLookup = useMemo(() => {
    const map = new Map<string, Entity>();
    const safeDataTypes = Array.isArray(dataTypes) ? dataTypes : [];
    safeDataTypes.forEach((dt) => {
      map.set(dt.id.toString(), dt);
      if (dt.data_type_id) map.set(dt.data_type_id, dt);
    });
    return map;
  }, [dataTypes]);

  // Fetch functions for ServerSearchableDropdown
  const fetchStudies = useCallback(async (search: string, page: number, perPage: number) => {
    const response = await apiClient.searchAdminStudies(search, page, perPage);
    if (response.error) throw new Error(response.error);
    return response.data as PaginatedResponse<StudySummary>;
  }, []);

  const fetchClinics = useCallback(async (search: string, page: number, perPage: number) => {
    const response = await apiClient.searchAdminClinics(search, page, perPage);
    if (response.error) throw new Error(response.error);
    return response.data as PaginatedResponse<ClinicSummary>;
  }, []);

  const fetchDevices = useCallback(async (search: string, page: number, perPage: number) => {
    const response = await apiClient.searchAdminDevices(search, page, perPage);
    if (response.error) throw new Error(response.error);
    return response.data as PaginatedResponse<DeviceSummary>;
  }, []);

  const fetchDataTypes = useCallback(async (search: string, page: number, perPage: number) => {
    const response = await apiClient.searchAdminDataTypes(search, page, perPage);
    if (response.error) throw new Error(response.error);
    return response.data as PaginatedResponse<DataTypeSummary>;
  }, []);

  // Get display name for entity
  const getEntityName = (id: string, lookup: Map<string, Entity>, fallback: string): string => {
    const entity = lookup.get(id) || lookup.get(id.toLowerCase());
    return entity?.name || entity?.study_id || entity?.clinic_id || entity?.device_id || entity?.data_type_id || fallback;
  };

  const getUserDisplay = (email: string): string => {
    const user = userLookup.get(email.toLowerCase());
    return user?.email || email;
  };

  const getUserGroupDisplay = (name: string): string => {
    const group = userGroupLookup.get(name);
    return group?.name || name;
  };

  const getRoleDisplay = (name: string): string => {
    const role = roleLookup.get(name);
    return role?.name || name;
  };

  const handleAddStatement = () => {
    // Build statement from current form
    const statement: PolicyStatement = {
      Effect: currentStatement.effect,
      Resource: {},
    };

    // Add Principal if specified
    const hasPrincipal =
      currentStatement.principal.users.length > 0 ||
      currentStatement.principal.userGroups.length > 0 ||
      currentStatement.principal.roles.length > 0;

    if (hasPrincipal) {
      statement.Principal = {};
      if (currentStatement.principal.users.length > 0) {
        statement.Principal.Users = currentStatement.principal.users;
      }
      if (currentStatement.principal.userGroups.length > 0) {
        statement.Principal.UserGroups = currentStatement.principal.userGroups;
      }
      if (currentStatement.principal.roles.length > 0) {
        statement.Principal.Roles = currentStatement.principal.roles;
      }
    }

    // Add Resources
    if (currentStatement.studies.length > 0) {
      statement.Resource.Studies = currentStatement.studies.map((s) => ({
        study_id: s.id,
        ...(s.start && { start_timestamp: s.start }),
        ...(s.end && { end_timestamp: s.end }),
      }));
    }

    if (currentStatement.clinics.length > 0) {
      statement.Resource.Clinics = currentStatement.clinics.map((c) => ({
        clinic_id: c.id,
        ...(c.start && { start_timestamp: c.start }),
        ...(c.end && { end_timestamp: c.end }),
      }));
    }

    if (currentStatement.devices.length > 0) {
      statement.Resource.Devices = currentStatement.devices.map((d) => ({
        device_id: d.id,
        ...(d.start && { start_timestamp: d.start }),
        ...(d.end && { end_timestamp: d.end }),
      }));
    }

    if (currentStatement.dataTypes.length > 0) {
      statement.Resource.DataTypes = currentStatement.dataTypes.map((dt) => ({
        data_type_id: dt.id,
      }));
    }

    // Add statement to list
    setStatements([...statements, statement]);

    // Reset form
    setCurrentStatement({
      effect: 'Allow',
      principal: {
        users: [],
        userGroups: [],
        roles: [],
      },
      studies: [],
      clinics: [],
      devices: [],
      dataTypes: [],
    });
  };

  const handleRemoveStatement = (index: number) => {
    setStatements(statements.filter((_, i) => i !== index));
  };

  const isStatementValid = (): boolean => {
    // Must have at least one resource
    return (
      currentStatement.studies.length > 0 ||
      currentStatement.clinics.length > 0 ||
      currentStatement.devices.length > 0 ||
      currentStatement.dataTypes.length > 0
    );
  };

  // Resource count for a statement
  const getResourceCount = (stmt: PolicyStatement): number => {
    return (
      (stmt.Resource.Studies?.length || 0) +
      (stmt.Resource.Clinics?.length || 0) +
      (stmt.Resource.Devices?.length || 0) +
      (stmt.Resource.DataTypes?.length || 0)
    );
  };

  // Principal count for a statement
  const getPrincipalCount = (stmt: PolicyStatement): number => {
    if (!stmt.Principal) return 0;
    return (
      (stmt.Principal.Users?.length || 0) +
      (stmt.Principal.UserGroups?.length || 0) +
      (stmt.Principal.Roles?.length || 0)
    );
  };

  return (
    <div className="policy-builder">
      {/* Current Statement Builder */}
      <Card className="mb-4">
        <Card.Header>
          <strong>{t('admin.policyManagement.buildStatement', 'Build Statement')}</strong>
        </Card.Header>
        <Card.Body>
          {/* Effect Selection */}
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.policyManagement.effect', 'Effect')}</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                label="Allow"
                name="effect"
                id="effect-allow"
                checked={currentStatement.effect === 'Allow'}
                onChange={() =>
                  setCurrentStatement({ ...currentStatement, effect: 'Allow' })
                }
              />
              <Form.Check
                inline
                type="radio"
                label="Deny"
                name="effect"
                id="effect-deny"
                checked={currentStatement.effect === 'Deny'}
                onChange={() =>
                  setCurrentStatement({ ...currentStatement, effect: 'Deny' })
                }
              />
            </div>
            <Form.Text className="text-muted">
              {currentStatement.effect === 'Allow'
                ? t('admin.policyManagement.allowHelp', 'Grant access to specified resources')
                : t('admin.policyManagement.denyHelp', 'Explicitly deny access (overrides Allow)')}
            </Form.Text>
          </Form.Group>

          {/* Principal Selection */}
          <div className="mb-3">
            <h6>{t('admin.policyManagement.principals', 'Principals (Optional)')}</h6>
            <Alert variant="info" className="small">
              {t(
                'admin.policyManagement.principalsHelp',
                'If no principals are specified, the statement applies to everyone.'
              )}
            </Alert>

            <PrincipalSelector
              type="users"
              label={t('admin.policyManagement.users', 'Users')}
              selected={currentStatement.principal.users}
              onAdd={(email) =>
                setCurrentStatement({
                  ...currentStatement,
                  principal: {
                    ...currentStatement.principal,
                    users: [...currentStatement.principal.users, email],
                  },
                })
              }
              onRemove={(email) =>
                setCurrentStatement({
                  ...currentStatement,
                  principal: {
                    ...currentStatement.principal,
                    users: currentStatement.principal.users.filter((u) => u !== email),
                  },
                })
              }
              color="primary"
              getDisplayValue={getUserDisplay}
              onRefresh={onRefreshUsers}
              isRefreshing={isRefreshingUsers}
            />

            <PrincipalSelector
              type="userGroups"
              label={t('admin.policyManagement.userGroups', 'User Groups')}
              selected={currentStatement.principal.userGroups}
              onAdd={(name) =>
                setCurrentStatement({
                  ...currentStatement,
                  principal: {
                    ...currentStatement.principal,
                    userGroups: [...currentStatement.principal.userGroups, name],
                  },
                })
              }
              onRemove={(name) =>
                setCurrentStatement({
                  ...currentStatement,
                  principal: {
                    ...currentStatement.principal,
                    userGroups: currentStatement.principal.userGroups.filter((g) => g !== name),
                  },
                })
              }
              color="info"
              getDisplayValue={getUserGroupDisplay}
              onRefresh={onRefreshUserGroups}
              isRefreshing={isRefreshingUserGroups}
            />

            <PrincipalSelector
              type="roles"
              label={t('admin.policyManagement.roles', 'Roles')}
              selected={currentStatement.principal.roles}
              onAdd={(name) =>
                setCurrentStatement({
                  ...currentStatement,
                  principal: {
                    ...currentStatement.principal,
                    roles: [...currentStatement.principal.roles, name],
                  },
                })
              }
              onRemove={(name) =>
                setCurrentStatement({
                  ...currentStatement,
                  principal: {
                    ...currentStatement.principal,
                    roles: currentStatement.principal.roles.filter((r) => r !== name),
                  },
                })
              }
              color="success"
              getDisplayValue={getRoleDisplay}
              onRefresh={onRefreshRoles}
              isRefreshing={isRefreshingRoles}
            />
          </div>

          {/* Resource Selection */}
          <div className="mb-3">
            <h6>{t('admin.policyManagement.resources', 'Resources (Required)')}</h6>
            <Alert variant="warning" className="small">
              {t(
                'admin.policyManagement.resourcesHelp',
                'Select at least one resource type. For Studies, Clinics, and Devices, you can optionally specify time windows.'
              )}
            </Alert>

            {/* Studies */}
            <Card className="mb-2">
              <Card.Header className="py-2">
                <small>{t('admin.policyManagement.studies', 'Studies')}</small>
              </Card.Header>
              <Card.Body className="py-2">
                <Row>
                  <Col md={6}>
                    <ServerSearchableDropdown<StudySummary>
                      fetchData={fetchStudies}
                      value=""
                      onChange={(value: string, selectedOption: StudySummary | undefined) => {
                        if (value && selectedOption) {
                          setCurrentStatement({
                            ...currentStatement,
                            studies: [
                              ...currentStatement.studies,
                              { id: selectedOption.study_id || selectedOption.id.toString() },
                            ],
                          });
                        }
                      }}
                      getOptionValue={(s) => s.id.toString()}
                      getOptionLabel={(s) => s.study_id || s.name || `Study ${s.id}`}
                      placeholder="Select study..."
                      size="sm"
                      onRefresh={onRefreshStudies}
                      isRefreshing={isRefreshingStudies}
                    />
                  </Col>
                </Row>
                <div className="mt-2">
                  {currentStatement.studies.map((study, idx) => (
                    <Badge key={idx} bg="secondary" className="me-2 mb-2">
                      {getEntityName(study.id, studyLookup, study.id)}
                      <Button
                        size="sm"
                        variant="link"
                        className="text-white p-0 ms-2"
                        onClick={() =>
                          setCurrentStatement({
                            ...currentStatement,
                            studies: currentStatement.studies.filter((_, i) => i !== idx),
                          })
                        }
                      >
                        ×
                      </Button>
                    </Badge>
                  ))}
                </div>
              </Card.Body>
            </Card>

            {/* Clinics */}
            <Card className="mb-2">
              <Card.Header className="py-2">
                <small>{t('admin.policyManagement.clinics', 'Clinics')}</small>
              </Card.Header>
              <Card.Body className="py-2">
                <Row>
                  <Col md={6}>
                    <ServerSearchableDropdown<ClinicSummary>
                      fetchData={fetchClinics}
                      value=""
                      onChange={(value: string, selectedOption: ClinicSummary | undefined) => {
                        if (value && selectedOption) {
                          setCurrentStatement({
                            ...currentStatement,
                            clinics: [
                              ...currentStatement.clinics,
                              { id: selectedOption.clinic_id || selectedOption.id.toString() },
                            ],
                          });
                        }
                      }}
                      getOptionValue={(c) => c.id.toString()}
                      getOptionLabel={(c) => c.clinic_id || c.name || `Clinic ${c.id}`}
                      placeholder="Select clinic..."
                      size="sm"
                      onRefresh={onRefreshClinics}
                      isRefreshing={isRefreshingClinics}
                    />
                  </Col>
                </Row>
                <div className="mt-2">
                  {currentStatement.clinics.map((clinic, idx) => (
                    <Badge key={idx} bg="secondary" className="me-2 mb-2">
                      {getEntityName(clinic.id, clinicLookup, clinic.id)}
                      <Button
                        size="sm"
                        variant="link"
                        className="text-white p-0 ms-2"
                        onClick={() =>
                          setCurrentStatement({
                            ...currentStatement,
                            clinics: currentStatement.clinics.filter((_, i) => i !== idx),
                          })
                        }
                      >
                        ×
                      </Button>
                    </Badge>
                  ))}
                </div>
              </Card.Body>
            </Card>

            {/* Devices */}
            <Card className="mb-2">
              <Card.Header className="py-2">
                <small>{t('admin.policyManagement.devices', 'Devices')}</small>
              </Card.Header>
              <Card.Body className="py-2">
                <Row>
                  <Col md={6}>
                    <ServerSearchableDropdown<DeviceSummary>
                      fetchData={fetchDevices}
                      value=""
                      onChange={(value: string, selectedOption: DeviceSummary | undefined) => {
                        if (value && selectedOption) {
                          setCurrentStatement({
                            ...currentStatement,
                            devices: [
                              ...currentStatement.devices,
                              { id: selectedOption.device_id || selectedOption.id.toString() },
                            ],
                          });
                        }
                      }}
                      getOptionValue={(d) => d.id.toString()}
                      getOptionLabel={(d) => d.device_id || d.name || `Device ${d.id}`}
                      placeholder="Select device..."
                      size="sm"
                      onRefresh={onRefreshDevices}
                      isRefreshing={isRefreshingDevices}
                    />
                  </Col>
                </Row>
                <div className="mt-2">
                  {currentStatement.devices.map((device, idx) => (
                    <Badge key={idx} bg="secondary" className="me-2 mb-2">
                      {getEntityName(device.id, deviceLookup, device.id)}
                      <Button
                        size="sm"
                        variant="link"
                        className="text-white p-0 ms-2"
                        onClick={() =>
                          setCurrentStatement({
                            ...currentStatement,
                            devices: currentStatement.devices.filter((_, i) => i !== idx),
                          })
                        }
                      >
                        ×
                      </Button>
                    </Badge>
                  ))}
                </div>
              </Card.Body>
            </Card>

            {/* Data Types */}
            <Card className="mb-2">
              <Card.Header className="py-2">
                <small>{t('admin.policyManagement.dataTypes', 'Data Types')}</small>
              </Card.Header>
              <Card.Body className="py-2">
                <Row>
                  <Col md={6}>
                    <ServerSearchableDropdown<DataTypeSummary>
                      fetchData={fetchDataTypes}
                      value=""
                      onChange={(value: string, selectedOption: DataTypeSummary | undefined) => {
                        if (value && selectedOption) {
                          setCurrentStatement({
                            ...currentStatement,
                            dataTypes: [
                              ...currentStatement.dataTypes,
                              { id: selectedOption.name || selectedOption.id.toString() },
                            ],
                          });
                        }
                      }}
                      getOptionValue={(dt) => dt.id.toString()}
                      getOptionLabel={(dt) => dt.name || `DataType ${dt.id}`}
                      placeholder="Select data type..."
                      size="sm"
                      onRefresh={onRefreshDataTypes}
                      isRefreshing={isRefreshingDataTypes}
                    />
                  </Col>
                </Row>
                <div className="mt-2">
                  {currentStatement.dataTypes.map((dataType, idx) => (
                    <Badge key={idx} bg="secondary" className="me-2 mb-2">
                      {getEntityName(dataType.id, dataTypeLookup, dataType.id)}
                      <Button
                        size="sm"
                        variant="link"
                        className="text-white p-0 ms-2"
                        onClick={() =>
                          setCurrentStatement({
                            ...currentStatement,
                            dataTypes: currentStatement.dataTypes.filter((_, i) => i !== idx),
                          })
                        }
                      >
                        ×
                      </Button>
                    </Badge>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* Add Statement Button */}
          <Button
            variant="primary"
            onClick={handleAddStatement}
            disabled={!isStatementValid()}
          >
            {t('admin.policyManagement.addStatement', 'Add Statement')}
          </Button>
          {!isStatementValid() && (
            <Form.Text className="text-danger d-block mt-2">
              {t(
                'admin.policyManagement.selectResource',
                'Please select at least one resource to add this statement.'
              )}
            </Form.Text>
          )}
        </Card.Body>
      </Card>

      {/* Statements List */}
      {statements.length > 0 && (
        <Card>
          <Card.Header>
            <strong>
              {t('admin.policyManagement.statements', 'Policy Statements')} ({statements.length})
            </strong>
          </Card.Header>
          <Card.Body className="p-0">
            <Table striped hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('admin.policyManagement.effect', 'Effect')}</th>
                  <th>{t('admin.policyManagement.principals', 'Principals')}</th>
                  <th>{t('admin.policyManagement.resources', 'Resources')}</th>
                  <th>{t('admin.policyManagement.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {statements.map((stmt, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>
                      <Badge bg={stmt.Effect === 'Allow' ? 'success' : 'danger'}>
                        {stmt.Effect}
                      </Badge>
                    </td>
                    <td>
                      {getPrincipalCount(stmt) > 0 ? (
                        <span className="text-muted">
                          {getPrincipalCount(stmt)} principal(s)
                        </span>
                      ) : (
                        <span className="text-muted fst-italic">Everyone</span>
                      )}
                    </td>
                    <td>
                      <span className="text-muted">{getResourceCount(stmt)} resource(s)</span>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleRemoveStatement(idx)}
                      >
                        {t('admin.policyManagement.remove', 'Remove')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {statements.length === 0 && (
        <Alert variant="info">
          {t(
            'admin.policyManagement.noStatements',
            'No statements added yet. Build a statement above to get started.'
          )}
        </Alert>
      )}
    </div>
  );
}
